import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, type AppUser, type ChatDialog } from '../api/client';
import { disconnectChatSocket, getChatSocket } from '../api/chatSocket';
import i18n from '../i18n';
import {
  type AppMode,
  detectAppMode,
  getStoredToken,
  getTelegramInitData,
  initTelegramWebApp,
  markTelegramSession,
  storeRefreshToken,
  storeToken,
} from '../utils/platform';
import { subscribeSession } from '../api/core/auth-session';
import { captureReferralCode, getPendingReferralCode, storePendingReferralCode } from '../utils/referral';
import { bindTelegramViewportSync } from '../utils/viewport';

interface DisplayUser {
  first_name?: string;
  username?: string;
  email?: string;
}

interface AuthContextValue {
  ready: boolean;
  error: string | null;
  token: string | null;
  mode: AppMode;
  user: DisplayUser | null;
  profile: AppUser | null;
  isAuthenticated: boolean;
  unreadNotifications: number;
  unreadSupportChat: number;
  refreshProfile: () => Promise<AppUser | null>;
  updateProfile: (input: { name?: string; surname?: string }) => Promise<AppUser>;
  uploadAvatar: (file: File) => Promise<AppUser>;
  refreshUnreadNotifications: () => Promise<void>;
  refreshUnreadSupportChat: () => Promise<void>;
  clearUnreadSupportChat: () => void;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (input: {
    email: string;
    password: string;
    name?: string;
  }) => Promise<{ access_token: string; refresh_token?: string }>;
  signInWithTokens: (tokens: { access_token: string; refresh_token?: string }) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  loginWithTelegramWidget: (payload: {
    id: number;
    first_name?: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
    auth_date: number;
    hash: string;
  }) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<{ ok: boolean; alreadyVerified?: boolean }>;
  resendVerification: (email: string) => Promise<void>;
  logout: () => void;
  /** Password accounts that still need to confirm email. OAuth/Telegram are treated as verified. */
  needsEmailVerification: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  ready: false,
  error: null,
  token: null,
  mode: 'web',
  user: null,
  profile: null,
  isAuthenticated: false,
  unreadNotifications: 0,
  unreadSupportChat: 0,
  refreshProfile: async () => null,
  updateProfile: async () => {
    throw new Error('not ready');
  },
  uploadAvatar: async () => {
    throw new Error('not ready');
  },
  refreshUnreadNotifications: async () => undefined,
  refreshUnreadSupportChat: async () => undefined,
  clearUnreadSupportChat: () => undefined,
  loginWithEmail: async () => undefined,
  registerWithEmail: async () => ({ access_token: '' }),
  signInWithTokens: async () => undefined,
  loginWithGoogle: async () => undefined,
  loginWithTelegramWidget: async () => undefined,
  forgotPassword: async () => undefined,
  resetPassword: async () => undefined,
  verifyEmail: async () => ({ ok: false }),
  resendVerification: async () => undefined,
  logout: () => undefined,
  needsEmailVerification: false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const mode = useMemo(() => detectAppMode(), []);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<DisplayUser | null>(null);
  const [profile, setProfile] = useState<AppUser | null>(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadSupportChat, setUnreadSupportChat] = useState(0);

  const applyToken = useCallback((accessToken: string | null, persist: boolean, refreshToken?: string | null) => {
    if (accessToken) {
      api.setToken(accessToken);
      setToken(accessToken);
      if (persist) {
        storeToken(accessToken);
        if (refreshToken !== undefined) storeRefreshToken(refreshToken);
      }
    } else {
      api.clearToken();
      setToken(null);
      if (persist) {
        storeToken(null);
        storeRefreshToken(null);
      }
    }
  }, []);

  const refreshUnreadNotifications = useCallback(async () => {
    try {
      const res = await api.getNotifications();
      setUnreadNotifications((res.data || []).filter((n) => !n.readAt).length);
    } catch {
      /* ignore — badge just stays at its last known value */
    }
  }, []);

  const refreshUnreadSupportChat = useCallback(async () => {
    try {
      const payload = await api.getSupportChat(1);
      setUnreadSupportChat(Math.max(0, Number(payload.dialog?.userUnreadCount || 0)));
    } catch {
      /* ignore */
    }
  }, []);

  const clearUnreadSupportChat = useCallback(() => {
    setUnreadSupportChat(0);
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const me = await api.getMe();
      setProfile(me);
      setUser({
        first_name: me.firstName || undefined,
        username: me.username || undefined,
        email: me.email || undefined,
      });
      void refreshUnreadNotifications();
      void refreshUnreadSupportChat();
      return me;
    } catch {
      return null;
    }
  }, [refreshUnreadNotifications, refreshUnreadSupportChat]);

  const updateProfile = useCallback(async (input: { name?: string; surname?: string }) => {
    const me = await api.updateMe(input);
    setProfile(me);
    setUser({
      first_name: me.firstName || undefined,
      username: me.username || undefined,
      email: me.email || undefined,
    });
    return me;
  }, []);

  const uploadAvatar = useCallback(async (file: File) => {
    const me = await api.uploadMyAvatar(file);
    setProfile(me);
    setUser({
      first_name: me.firstName || undefined,
      username: me.username || undefined,
      email: me.email || undefined,
    });
    return me;
  }, []);

  /** Drop prior identity before applying a new token so chat/profile cannot leak across accounts. */
  const beginSession = useCallback(
    (tokens: { access_token: string; refresh_token?: string }, persist: boolean) => {
      disconnectChatSocket();
      setProfile(null);
      setUser(null);
      setUnreadNotifications(0);
      setUnreadSupportChat(0);
      applyToken(tokens.access_token, persist, tokens.refresh_token ?? null);
    },
    [applyToken],
  );

  const bootstrapTelegram = useCallback(async () => {
    markTelegramSession();
    initTelegramWebApp();
    captureReferralCode();
    const tg = window.Telegram?.WebApp;
    setUser(tg?.initDataUnsafe?.user ?? null);

    let initData = getTelegramInitData();
    // First open: SDK / hash can land a beat after React mounts. One short retry
    // is enough — returning users already have a stored JWT as fallback.
    if (!initData) {
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      initTelegramWebApp();
      initData = getTelegramInitData();
    }
    const referralCode = getPendingReferralCode() || undefined;

    // Prefer fresh Telegram login when initData is present.
    if (initData) {
      try {
        const res = await api.login(initData, referralCode);
        // Persist so Telegram Web (weba) refresh still has a session after
        // Telegram clears the launch hash / initData.
        beginSession(res, true);
        await refreshProfile();
        storePendingReferralCode(null);
        setError(null);
        setReady(true);
        return;
      } catch (err) {
        // Fall through to stored session before failing hard — refresh on weba
        // can race or briefly present stale initData.
        const savedAfterFail = getStoredToken();
        if (!savedAfterFail) {
          setError(err instanceof Error ? err.message : i18n.t('auth.authFailed'));
          setReady(true);
          return;
        }
      }
    }

    // Telegram Web refresh often drops initData; reuse the last Mini App token.
    const saved = getStoredToken();
    if (saved) {
      applyToken(saved, false);
      try {
        const me = await api.getMe();
        setProfile(me);
        setUser({
          first_name: me.firstName || undefined,
          username: me.username || undefined,
          email: me.email || undefined,
        });
        void refreshUnreadNotifications();
        void refreshUnreadSupportChat();
        setError(null);
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403 || /jwt expired/i.test(err.body || err.message))) {
          applyToken(null, true);
          setProfile(null);
          setUser(null);
          setUnreadSupportChat(0);
          setError(i18n.t('auth.openViaBot'));
        }
        // Network/5xx: keep the token; App will retry via normal API calls.
      }
      setReady(true);
      return;
    }

    setError(i18n.t('auth.openViaBot'));
    setReady(true);
  }, [applyToken, beginSession, refreshProfile, refreshUnreadNotifications, refreshUnreadSupportChat]);

  const bootstrapWeb = useCallback(async () => {
    const saved = getStoredToken();
    if (!saved) {
      setReady(true);
      return;
    }

    applyToken(saved, false);
    try {
      const me = await api.getMe();
      setProfile(me);
      setUser({
        first_name: me.firstName || undefined,
        username: me.username || undefined,
        email: me.email || undefined,
      });
      void refreshUnreadNotifications();
      void refreshUnreadSupportChat();
    } catch (err) {
      // Only drop the session when the token is actually rejected. Network/5xx
      // used to wipe a fresh Google/Telegram login and bounce the user back to /login.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403 || /jwt expired/i.test(err.body || err.message))) {
        applyToken(null, true);
        setProfile(null);
        setUser(null);
        setUnreadSupportChat(0);
      }
    }
    setReady(true);
  }, [applyToken, refreshUnreadNotifications, refreshUnreadSupportChat]);

  useEffect(() => {
    captureReferralCode();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.appMode = mode;
    if (mode === 'telegram') {
      bindTelegramViewportSync();
    }
    return () => {
      delete document.documentElement.dataset.appMode;
    };
  }, [mode]);

  useEffect(() => {
    if (mode === 'telegram') {
      void bootstrapTelegram();
    } else {
      void bootstrapWeb();
    }
  }, [mode, bootstrapTelegram, bootstrapWeb]);

  useEffect(() => {
    return subscribeSession((event) => {
      if (event.type === 'cleared') {
        disconnectChatSocket();
        api.clearToken();
        setToken(null);
        setProfile(null);
        setUser(null);
        setUnreadNotifications(0);
        setUnreadSupportChat(0);
        return;
      }
      api.setToken(event.accessToken);
      setToken(event.accessToken);
    });
  }, []);

  // Keep a lightweight chat socket while logged in so support replies bump the unread badge live.
  useEffect(() => {
    if (!token) return;
    const socket = getChatSocket(token);
    const onDialogUpdated = (dialog: ChatDialog) => {
      if (typeof dialog?.userUnreadCount === 'number') {
        setUnreadSupportChat(Math.max(0, dialog.userUnreadCount));
      }
    };
    socket.on('dialog:updated', onDialogUpdated);
    void refreshUnreadSupportChat();
    return () => {
      socket.off('dialog:updated', onDialogUpdated);
    };
  }, [token, refreshUnreadSupportChat]);

  const consumeReferralCode = useCallback(() => {
    storePendingReferralCode(null);
  }, []);

  const loginWithEmail = useCallback(
    async (email: string, password: string) => {
      const res = await api.loginEmail(email, password);
      beginSession(res, true);
      setUser({ email, first_name: email.split('@')[0] });
      await refreshProfile();
      consumeReferralCode();
      setError(null);
    },
    [beginSession, refreshProfile, consumeReferralCode],
  );

  const registerWithEmail = useCallback(
    async (input: { email: string; password: string; name?: string }) => {
      const res = await api.registerEmail({ ...input, referralCode: getPendingReferralCode() || undefined });
      beginSession(res, true);
      setUser({ email: input.email, first_name: input.name || input.email.split('@')[0] });
      await refreshProfile();
      consumeReferralCode();
      setError(null);
      return res;
    },
    [beginSession, refreshProfile, consumeReferralCode],
  );

  const signInWithTokens = useCallback(
    async (tokens: { access_token: string; refresh_token?: string }) => {
      beginSession(tokens, true);
      await refreshProfile();
      setError(null);
    },
    [beginSession, refreshProfile],
  );

  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      const res = await api.loginGoogle(idToken, getPendingReferralCode() || undefined);
      beginSession(res, true);
      await refreshProfile();
      consumeReferralCode();
      setError(null);
    },
    [beginSession, refreshProfile, consumeReferralCode],
  );

  const loginWithTelegramWidget = useCallback(
    async (payload: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
      photo_url?: string;
      auth_date: number;
      hash: string;
    }) => {
      const res = await api.loginTelegramWidget({ ...payload, referralCode: getPendingReferralCode() || undefined });
      beginSession(res, true);
      setUser({ first_name: payload.first_name, username: payload.username });
      await refreshProfile();
      consumeReferralCode();
      setError(null);
    },
    [beginSession, refreshProfile, consumeReferralCode],
  );

  const forgotPassword = useCallback(async (email: string) => {
    await api.forgotPassword(email);
  }, []);

  const resetPassword = useCallback(async (token: string, password: string) => {
    await api.resetPassword(token, password);
  }, []);

  const verifyEmail = useCallback(
    async (token: string) => {
      const res = await api.verifyEmail(token);
      await refreshProfile();
      return res;
    },
    [refreshProfile],
  );

  const resendVerification = useCallback(async (email: string) => {
    await api.resendVerification(email);
  }, []);

  const logout = useCallback(() => {
    disconnectChatSocket();
    applyToken(null, true);
    setProfile(null);
    setUser(null);
    setUnreadNotifications(0);
    setUnreadSupportChat(0);
    setError(null);
  }, [applyToken]);

  const needsEmailVerification = Boolean(
    profile &&
      !profile.emailVerified &&
      !profile.tg_id &&
      profile.email &&
      !profile.email.endsWith('@cozzysim.local'),
  );

  return (
    <AuthContext.Provider
      value={{
        ready,
        error,
        token,
        mode,
        user,
        profile,
        isAuthenticated: Boolean(token),
        unreadNotifications,
        unreadSupportChat,
        refreshProfile,
        updateProfile,
        uploadAvatar,
        refreshUnreadNotifications,
        refreshUnreadSupportChat,
        clearUnreadSupportChat,
        loginWithEmail,
        registerWithEmail,
        signInWithTokens,
        loginWithGoogle,
        loginWithTelegramWidget,
        forgotPassword,
        resetPassword,
        verifyEmail,
        resendVerification,
        logout,
        needsEmailVerification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        initData: string;
        colorScheme?: 'light' | 'dark';
        /** 'android' | 'ios' | 'macos' | 'tdesktop' | 'web' | 'weba' | 'unknown' | ... */
        platform?: string;
        initDataUnsafe: {
          user?: {
            first_name?: string;
            username?: string;
            language_code?: string;
          };
          start_param?: string;
        };
        ready: () => void;
        expand: () => void;
        /** Bot API 8.0+ */
        requestFullscreen?: () => void;
        setHeaderColor?: (color: string) => void;
        setBackgroundColor?: (color: string) => void;
        /** Bot API 7.7+ */
        disableVerticalSwipes?: () => void;
        /** Current visible height (CSS px); shrinks with the soft keyboard. */
        viewportHeight?: number;
        /** Height in the last stable state (CSS px); ignores transient keyboard. */
        viewportStableHeight?: number;
        isExpanded?: boolean;
        /** Bot API 8.0+ */
        safeAreaInset?: { top: number; bottom: number; left: number; right: number };
        /** Bot API 8.0+ */
        contentSafeAreaInset?: { top: number; bottom: number; left: number; right: number };
        close: () => void;
        onEvent?: (event: string, handler: () => void) => void;
        offEvent?: (event: string, handler: () => void) => void;
        MainButton: {
          text: string;
          show: () => void;
          hide: () => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
          showProgress: (leaveActive?: boolean) => void;
          hideProgress: () => void;
        };
        BackButton: {
          show: () => void;
          hide: () => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
        };
        openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
        openTelegramLink?: (url: string) => void;
        HapticFeedback?: {
          notificationOccurred?: (type: 'error' | 'success' | 'warning') => void;
          impactOccurred?: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
        };
      };
    };
  }
}
